import { lazy, Suspense, useRef, useState, type FormEvent, type MouseEvent } from "react";
import { SignInButton, useAuth } from "@clerk/react";
import { Images, Loader2, Plus, X } from "lucide-react";
import { authedFetch, readJson, useUserGalleries } from "./api";
import type { Gallery } from "./model";
import { AuthControls, LoadingView } from "./ui";
import { useAccountAppearance } from "./AccountAppearance";
import { AlbumFontPicker } from "./AlbumFontPicker";
import { AlbumTitle } from "./AlbumTitle";
import { hasSessionHint } from "./session";
import "./HomePage.css";
import { navigate } from "./navigation";

const loadLandingPage = () => import("./LandingPage");
// Fetch the landing page alongside Clerk for likely visitors instead of after it.
if (typeof window !== "undefined" && window.location.pathname === "/" && !hasSessionHint()) void loadLandingPage();
const LandingPage = lazy(() => loadLandingPage().then((module) => ({ default: module.LandingPage })));

export function HomePage({ onOpenGallery, openingSlug }: {
  onOpenGallery?: (event: MouseEvent<HTMLAnchorElement>, gallery: Gallery) => void;
  openingSlug?: string;
}) {
  const appearance = useAccountAppearance();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { galleries, error: galleriesError, loading } = useUserGalleries();
  const dialog = useRef<HTMLDialogElement>(null);
  const submitting = useRef(false);
  const [displayName, setDisplayName] = useState("");
  const [titleFont, setTitleFont] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");

  // Visitors without a session see the landing page at once; returning users
  // wait for Clerk so their library never flashes the marketing page.
  if (!isSignedIn && (isLoaded || !hasSessionHint())) {
    return <Suspense fallback={<main className="landing-pending" aria-busy="true" />}><LandingPage /></Suspense>;
  }
  if (!isLoaded) return <LoadingView />;

  function openCreateDialog() {
    setDisplayName("");
    setTitleFont("");
    setError("");
    dialog.current?.showModal();
  }

  async function createGallery(event: FormEvent) {
    event.preventDefault();
    if (!isSignedIn || submitting.current) return;

    submitting.current = true;
    setIsCreating(true);
    setError("");
    try {
      const response = await authedFetch(getToken, "/api/galleries", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ displayName: displayName.trim() || "Untitled Gallery", titleFont: titleFont || null })
      });
      const data = await readJson<{ gallery: Gallery }>(response);
      navigate(`/g/${data.gallery.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create gallery");
      submitting.current = false;
      setIsCreating(false);
    }
  }

  const createTile = (
    <button className="library-create" type="button" onClick={isSignedIn ? openCreateDialog : undefined}>
      <span className="library-stack library-stack-create" aria-hidden="true">
        <span className="library-print library-print-back" />
        <span className="library-print library-print-middle" />
        <span className="library-print library-print-front">
          <Plus aria-hidden="true" />
        </span>
      </span>
      <span className="library-caption">New gallery</span>
    </button>
  );

  return (
    <main className="library-shell" style={isSignedIn ? appearance.style : undefined}>
      <header className="library-topbar">
        <a className="brand" href="/">Pics Lot</a>
        <AuthControls />
      </header>

      <section className="library-content" aria-label="Galleries">
        {isSignedIn && galleriesError && <p className="error" role="alert">{galleriesError}</p>}
        <div className="library-grid">
          {isSignedIn ? createTile : <SignInButton mode="modal">{createTile}</SignInButton>}
          {isSignedIn && loading && (
            <div className="library-loading" role="status" aria-label="Loading galleries">
              <Loader2 className="spin" size={22} aria-hidden="true" />
            </div>
          )}
          {isSignedIn && !loading && galleries.map((gallery) => (
            <a className="library-gallery" href={`/g/${gallery.slug}`} key={gallery.slug} onClick={(event) => onOpenGallery?.(event, gallery)} aria-busy={openingSlug === gallery.slug || undefined}>
              <span className="library-stack" aria-hidden="true">
                <span className="library-print library-print-back">
                  {gallery.previewPhotoUrls?.[1] && <img src={gallery.previewPhotoUrls[1]} alt="" loading="lazy" decoding="async" />}
                </span>
                <span className="library-print library-print-middle">
                  {gallery.previewPhotoUrls?.[0] && <img src={gallery.previewPhotoUrls[0]} alt="" loading="lazy" decoding="async" />}
                </span>
                <span className="library-print library-print-front">
                  {gallery.coverPhotoUrl
                    ? <img className="library-cover-image" src={gallery.coverPhotoUrl} alt="" loading="lazy" decoding="async" />
                    : <span className="library-blank library-blank-empty"><Images size={30} strokeWidth={1.25} /></span>}
                </span>
              </span>
              <span className="library-caption"><AlbumTitle name={gallery.displayName} createdAt={gallery.createdAt} fontFamily={gallery.titleFont} /></span>
              {openingSlug === gallery.slug && <span className="library-opening" role="status"><Loader2 className="spin" size={14} />Opening gallery…</span>}
            </a>
          ))}
        </div>
      </section>

      <dialog
        className="library-dialog"
        ref={dialog}
        aria-labelledby="create-gallery-title"
        onCancel={(event) => { if (submitting.current) event.preventDefault(); }}
      >
        <form onSubmit={createGallery} aria-busy={isCreating}>
          <div className="library-dialog-heading">
            <h2 id="create-gallery-title">New gallery</h2>
            <button className="library-close" type="button" onClick={() => dialog.current?.close()} disabled={isCreating} aria-label="Close">
              <X size={20} />
            </button>
          </div>
          <label htmlFor="displayName">Gallery name</label>
          <input id="displayName" autoFocus autoComplete="off" value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Untitled Gallery" maxLength={80} disabled={isCreating} aria-describedby={error ? "create-gallery-error" : undefined} />
          <AlbumFontPicker value={titleFont} onChange={setTitleFont} name={displayName} disabled={isCreating} />
          {error && <p className="error" id="create-gallery-error" role="alert">{error}</p>}
          <button className="library-submit" type="submit" disabled={isCreating}>
            {isCreating ? <Loader2 className="spin" size={18} aria-hidden="true" /> : <Plus size={18} aria-hidden="true" />}
            {isCreating ? "Creating…" : "Create gallery"}
          </button>
        </form>
      </dialog>
    </main>
  );
}
